/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Référence produit (SKU) affichée en bas de la fiche produit.
 *
 * Focal ne met à jour son propre SKU que s'il est rendu à l'intérieur de <product-meta> (cf.
 * ProductMeta._updateSku dans theme.js). Comme on affiche la référence tout en bas de la colonne
 * d'infos — donc hors de ce conteneur — on la synchronise nous-mêmes sur `variant:changed`.
 *
 * Markup attendu (voir snippets/product-info.liquid) :
 *   <p data-louvini-sku-container data-form-id="…"><span data-louvini-sku-number>…</span></p>
 */
document.addEventListener('variant:changed', function(event) {
    const variant = event.detail.variant;
    const formId = event.target ? event.target.id : null;

    document.querySelectorAll('[data-louvini-sku-container]').forEach(function(container) {
        // Un même document peut porter plusieurs formulaires (quick-buy, produit vedette…) :
        // on ne met à jour que la référence liée au formulaire qui a émis l'événement.
        if (formId && container.dataset.formId && container.dataset.formId !== formId) {
            return;
        }

        const number = container.querySelector('[data-louvini-sku-number]');

        if (!variant || !variant.sku) {
            container.hidden = true;
            return;
        }

        if (number) {
            number.textContent = variant.sku;
        }

        container.hidden = false;
    });
});

/**
 * Zepto Product Personalizer → le cart drawer reste le panier.
 *
 * L'app est réglée en mode « 0 » : après l'ajout d'un produit personnalisé, elle fait
 * `window.location = "/cart"`. Elle appelle cependant `window.pplr_update_minicart` s'il existe, et saute
 * alors sa propre redirection (cf. canvas-script-with-view-v2.js de l'app). On s'en sert pour rester sur la
 * page et ouvrir le drawer. Sur la page /cart elle-même (où le drawer n'est pas rendu), on recharge.
 */
window.pplr_update_minicart = function() {
    if (document.body.classList.contains('template-cart')) {
        window.location.reload();
        return;
    }

    fetch(window.themeVariables.routes.cartUrl + '.js')
        .then(function(response) { return response.json(); })
        .then(function(cart) {
            document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart, openMiniCart: true } }));
        });
};

/**
 * <sticker-offer> — offre « stickers lettre offerts » (snippets/sticker-offer.liquid).
 *
 * - Ajoute les lettres choisies via /cart/add.js (une même lettre choisie deux fois = une ligne en
 *   quantité 2), en demandant la section `mini-cart` pour re-rendre le drawer sans second aller-retour.
 * - Dans le drawer, ce re-rendu remplace l'élément par son état serveur à jour.
 * - Hors drawer (context 'product', non utilisé à ce jour), le bloc n'est pas re-rendu : il se resynchronise
 *   sur `cart:updated` / `cart:refresh` (lettres restantes, ou confirmation) et ouvre le drawer.
 * - Pas de `is="loader-button"` sur le bouton : sur Safari, LoaderButton soumet le formulaire nativement
 *   (form.submit()), ce qui contournerait cet ajout AJAX.
 */
class StickerOffer extends HTMLElement {
    connectedCallback() {
        this.form = this.querySelector('.sticker-offer__form');
        this.errorElement = this.querySelector('[data-sticker-error]');
        this.onSubmit = this.onSubmit.bind(this);
        this.onCartChange = this.onCartChange.bind(this);

        if (this.form) {
            this.form.addEventListener('submit', this.onSubmit);
        }

        if (this.dataset.context === 'product') {
            document.documentElement.addEventListener('cart:updated', this.onCartChange);
            document.documentElement.addEventListener('cart:refresh', this.onCartChange);
        }
    }

    disconnectedCallback() {
        document.documentElement.removeEventListener('cart:updated', this.onCartChange);
        document.documentElement.removeEventListener('cart:refresh', this.onCartChange);
    }

    get quantity() {
        return parseInt(this.dataset.quantity, 10) || 2;
    }

    async onCartChange(event) {
        let cart = event.detail && event.detail.cart;

        if (!cart || !cart.items) {
            cart = await fetch(window.themeVariables.routes.cartUrl + '.js').then(function(response) { return response.json(); });
        }

        const productId = parseInt(this.dataset.productId, 10);
        const inCart = cart.items
            .filter(function(item) { return item.product_id === productId; })
            .reduce(function(total, item) { return total + item.quantity; }, 0);

        this.setRemaining(Math.max(0, this.quantity - inCart));
    }

    setRemaining(remaining) {
        this.querySelectorAll('[data-sticker-letter-field]').forEach(function(field, index) {
            const inactive = index >= remaining;
            field.hidden = inactive;
            field.querySelector('select').disabled = inactive;
        });

        this.toggleAttribute('data-complete', remaining === 0);
    }

    showError(message) {
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
    }

    async onSubmit(event) {
        event.preventDefault();
        this.errorElement.hidden = true;

        const selects = Array.from(this.form.querySelectorAll('select:not(:disabled)'));
        const missing = selects.find(function(select) { return !select.value; });

        if (missing) {
            this.showError(this.dataset.missingMessage);
            missing.focus();
            return;
        }

        const quantities = {};
        selects.forEach(function(select) {
            quantities[select.value] = (quantities[select.value] || 0) + 1;
        });

        const items = Object.keys(quantities).map(function(id) {
            return { id: parseInt(id, 10), quantity: quantities[id] };
        });

        const button = this.form.querySelector('[type="submit"]');
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');

        try {
            const response = await fetch(window.themeVariables.routes.cartAddUrl + '.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ items: items, sections: ['mini-cart'] })
            });
            const json = await response.json();

            if (!response.ok) {
                this.showError(json.description || this.dataset.errorMessage);
                return;
            }

            const cart = await fetch(window.themeVariables.routes.cartUrl + '.js').then(function(res) { return res.json(); });
            cart.sections = json.sections;

            document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true,
                detail: { cart: cart, openMiniCart: this.dataset.context === 'product' }
            }));
        } catch (error) {
            this.showError(this.dataset.errorMessage);
        } finally {
            button.disabled = false;
            button.removeAttribute('aria-busy');
        }
    }
}

if (!window.customElements.get('sticker-offer')) {
    window.customElements.define('sticker-offer', StickerOffer);
}

document.addEventListener('DOMContentLoaded', function() {
    document.body.addEventListener('change', function(event) {
        // Vérifie si l'élément modifié est celui avec l'ID 'giftWrap'
        if (event.target.id === 'giftWrap') {
            // Vérifie si la case est cochée
            if (event.target.checked) {
                let formData = {
                    'items': [{
                        'id': 53515793596764,
                        'quantity': 1
                    }]
                };
                fetch(window.Shopify.routes.root + 'cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(formData)
                })
                .then(response => {
                  if(document.body.classList.contains('template-cart')) {
                    window.location.href = "/cart";
                  } else {
                    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                        bubbles: true
                    }));
                  }
                    
                    return response.json();
                })
                .catch((error) => {
                    console.error('Error:', error);
                });
            }
        }
    });
});

